/**
 * iter101.4 — Admin view: impersonation audit log.
 *
 * Lists every `admin_impersonation_started` event (backed by
 * audit_logs) with search + role filter + date-window scrubber, so
 * we can reconstruct any support session: WHO signed in as WHOM,
 * WHEN, and from WHICH IP.
 *
 * Route: /admin/impersonation-log
 * API:   GET /api/admin/coe/impersonation-log?q&role&days&limit&skip
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { MagnifyingGlass, User, Shield, DeviceMobile, Globe, Clock } from "@phosphor-icons/react";

const ROLE_OPTIONS = [
    { value: "", label: "All roles" },
    { value: "doctor", label: "Doctor" },
    { value: "patient", label: "Patient" },
    { value: "staff", label: "CoE staff" },
    { value: "admin", label: "Admin" },
];
const DAYS_OPTIONS = [
    { value: 1, label: "Last 24 hours" },
    { value: 7, label: "Last 7 days" },
    { value: 30, label: "Last 30 days" },
    { value: 90, label: "Last 90 days" },
    { value: 0, label: "All time" },
];

function relative(iso) {
    try {
        const d = new Date(iso);
        const diff = Date.now() - d.getTime();
        if (diff < 60_000) return "just now";
        if (diff < 3_600_000) return `${Math.round(diff / 60_000)}m ago`;
        if (diff < 86_400_000) return `${Math.round(diff / 3_600_000)}h ago`;
        return `${Math.round(diff / 86_400_000)}d ago`;
    } catch { return ""; }
}

function browserOf(ua) {
    if (!ua) return "—";
    if (/Chrome\//.test(ua) && !/Edg\//.test(ua)) return "Chrome";
    if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) return "Safari";
    if (/Firefox\//.test(ua)) return "Firefox";
    if (/Edg\//.test(ua)) return "Edge";
    return "Other";
}
function platformOf(ua) {
    if (!ua) return "";
    if (/iPhone|iPad/.test(ua)) return "iOS";
    if (/Android/.test(ua)) return "Android";
    if (/Windows/.test(ua)) return "Windows";
    if (/Mac OS X|Macintosh/.test(ua)) return "Mac";
    if (/Linux/.test(ua)) return "Linux";
    return "";
}

const ROLE_TONE = {
    doctor: "bg-cyan-100 text-cyan-800",
    patient: "bg-violet-100 text-violet-800",
    staff: "bg-amber-100 text-amber-800",
    admin: "bg-rose-100 text-rose-800",
};

export default function ImpersonationLog() {
    const [q, setQ] = useState("");
    const [role, setRole] = useState("");
    const [days, setDays] = useState(7);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // Debounce the search box so we don't hammer the API on every keystroke.
    const [debouncedQ, setDebouncedQ] = useState("");
    useEffect(() => {
        const t = setTimeout(() => setDebouncedQ(q.trim()), 350);
        return () => clearTimeout(t);
    }, [q]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError("");
            try {
                const { data } = await api.get("/admin/coe/impersonation-log", {
                    params: { q: debouncedQ, role, days, limit: 200 },
                });
                if (cancelled) return;
                setRows(data.items || []);
                setTotal(data.total || 0);
            } catch (e) {
                if (cancelled) return;
                setError(e?.response?.data?.detail || "Failed to load audit log");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [debouncedQ, role, days]);

    const stats = useMemo(() => {
        const admins = new Set();
        const targets = new Set();
        for (const r of rows) {
            if (r.admin_id) admins.add(r.admin_id);
            if (r.target_id) targets.add(r.target_id);
        }
        return { sessions: rows.length, admins: admins.size, targets: targets.size };
    }, [rows]);

    return (
        <div className="space-y-6" data-testid="admin-impersonation-log">
            <header>
                <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Admin</div>
                <h1 className="font-display text-3xl font-semibold tracking-tight">Impersonation audit log</h1>
                <p className="text-sm text-muted-foreground max-w-2xl mt-1">
                    Every time an admin signs in as another user, we log it here — who, whom, when, and from which IP. Use this to reconstruct any support session or investigate an unexpected login.
                </p>
            </header>

            {/* Quick stats */}
            <div className="grid grid-cols-3 gap-3">
                <StatTile label="Sessions in view" value={stats.sessions} icon={<Clock size={16} />} />
                <StatTile label="Admins" value={stats.admins} icon={<Shield size={16} />} />
                <StatTile label="Distinct targets" value={stats.targets} icon={<User size={16} />} />
            </div>

            {/* Filters */}
            <div className="rounded-2xl border border-border bg-card p-4 flex flex-col md:flex-row md:items-center gap-3">
                <div className="relative flex-1 min-w-0">
                    <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search admin name, target name, or IP…"
                        className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
                        data-testid="impersonation-log-search"
                    />
                </div>
                <select
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    className="text-sm rounded-lg border border-border bg-background px-3 py-2"
                    data-testid="impersonation-log-role-filter"
                >
                    {ROLE_OPTIONS.map((r) => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                </select>
                <select
                    value={days}
                    onChange={(e) => setDays(Number(e.target.value))}
                    className="text-sm rounded-lg border border-border bg-background px-3 py-2"
                    data-testid="impersonation-log-window-filter"
                >
                    {DAYS_OPTIONS.map((r) => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                </select>
            </div>

            {/* Table */}
            <div className="rounded-2xl border border-border bg-card overflow-hidden">
                <div className="grid grid-cols-12 px-4 py-2.5 bg-muted/40 text-[10px] font-bold uppercase tracking-widest text-muted-foreground border-b border-border">
                    <div className="col-span-3">When</div>
                    <div className="col-span-3">Admin</div>
                    <div className="col-span-3">Target</div>
                    <div className="col-span-2">IP · Browser</div>
                    <div className="col-span-1 text-right">Role</div>
                </div>
                {loading && (
                    <div className="py-12 text-center text-xs text-muted-foreground">Loading audit log…</div>
                )}
                {!loading && error && (
                    <div className="py-12 text-center text-sm text-rose-700" data-testid="impersonation-log-error">{error}</div>
                )}
                {!loading && !error && rows.length === 0 && (
                    <div className="py-12 text-center text-sm text-muted-foreground" data-testid="impersonation-log-empty">
                        No impersonation sessions match these filters.
                    </div>
                )}
                {!loading && !error && rows.map((r) => (
                    <div
                        key={r.id}
                        className="grid grid-cols-12 px-4 py-3 border-b border-border last:border-b-0 text-xs items-start hover:bg-muted/20 transition-colors"
                        data-testid={`impersonation-row-${r.id}`}
                    >
                        <div className="col-span-3">
                            <div className="font-semibold tabular-nums">{new Date(r.created_at).toLocaleString()}</div>
                            <div className="text-[10px] text-muted-foreground">{relative(r.created_at)}</div>
                        </div>
                        <div className="col-span-3">
                            <div className="font-semibold truncate">{r.admin_name}</div>
                            <div className="text-[10px] text-muted-foreground">Admin</div>
                        </div>
                        <div className="col-span-3">
                            <div className="font-semibold truncate">{r.target_name}</div>
                            <div className="text-[10px] text-muted-foreground truncate">
                                {r.target_phone || r.target_id?.slice(0, 8)}
                                {r.target_staff_type ? ` · ${r.target_staff_type}` : ""}
                            </div>
                        </div>
                        <div className="col-span-2 min-w-0">
                            <div className="flex items-center gap-1 truncate">
                                <Globe size={11} className="text-muted-foreground shrink-0" />
                                <span className="font-mono text-[11px] truncate">{r.ip_address || "—"}</span>
                            </div>
                            <div className="text-[10px] text-muted-foreground flex items-center gap-1 mt-0.5 truncate">
                                <DeviceMobile size={10} />
                                {browserOf(r.user_agent)}
                                {platformOf(r.user_agent) ? ` · ${platformOf(r.user_agent)}` : ""}
                            </div>
                        </div>
                        <div className="col-span-1 text-right">
                            <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${ROLE_TONE[r.target_role] || "bg-zinc-100 text-zinc-700"}`}>
                                {r.target_role}
                            </span>
                        </div>
                    </div>
                ))}
            </div>

            <div className="text-[11px] text-muted-foreground">
                Showing {rows.length} of {total} sessions. Change filters to widen the search.
            </div>
        </div>
    );
}

function StatTile({ label, value, icon }) {
    return (
        <div className="rounded-2xl border border-border bg-card px-4 py-3 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">{icon}</div>
            <div>
                <div className="text-xl font-display font-bold tabular-nums">{value}</div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{label}</div>
            </div>
        </div>
    );
}
